/**
 * Single-file upload modal for one data-room item. Supports drag-drop or browse.
 * Delegates the actual upload to onUpload(item.id, file) and surfaces its error
 * message inline; closing is the caller's responsibility on success.
 */
import React, { useRef, useState } from 'react';

export default function UploadModal({ item, onUpload, onClose }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  async function handleFile(file) {
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      await onUpload(item.id, file);
    } catch (err) {
      setUploadError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  function handleInputChange(e) {
    const file = e.target.files[0];
    if (file) handleFile(file);
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button style={styles.close} onClick={onClose} aria-label="Close">&#x2715;</button>

        <h2 style={styles.title}>{item.number} Upload</h2>
        <p style={styles.description}>{item.description}</p>

        {uploadError && (
          <p style={styles.errorMsg}>{uploadError}</p>
        )}

        <div
          style={{
            ...styles.dropzone,
            ...(dragging ? styles.dropzoneDragging : {}),
          }}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <div style={styles.uploadIcon}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
              <path d="M12 3v10M7 8l5-5 5 5" stroke="var(--color-status-empty)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M4 17v1a2 2 0 002 2h12a2 2 0 002-2v-1" stroke="var(--color-status-empty)" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>

          {uploading ? (
            <p style={styles.dropText}>Uploading...</p>
          ) : (
            <>
              <p style={styles.dropText}>Drop files or click to upload</p>
              <button
                style={styles.browseBtn}
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
              >
                Browse
              </button>
            </>
          )}

          <input
            ref={inputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={handleInputChange}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
          />
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(33, 81, 45, 0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 16,
  },
  modal: {
    background: 'var(--color-frost)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--space-8)',
    width: '100%',
    maxWidth: 440,
    position: 'relative',
    boxShadow: 'var(--shadow-modal)',
  },
  close: {
    position: 'absolute',
    top: 16,
    right: 16,
    fontSize: 16,
    color: 'var(--color-forest)',
    opacity: 0.5,
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    lineHeight: 1,
  },
  title: {
    fontFamily: 'var(--font-display)',
    fontSize: 22,
    fontWeight: 800,
    color: 'var(--color-forest)',
    marginBottom: 'var(--space-3)',
  },
  description: {
    fontFamily: 'var(--font-body)',
    fontSize: 15,
    color: 'var(--color-forest)',
    lineHeight: 1.55,
    marginBottom: 'var(--space-6)',
    opacity: 0.85,
  },
  dropzone: {
    border: '2px dashed var(--color-mint-dark)',
    borderRadius: 'var(--radius-md)',
    background: 'var(--color-mint)',
    padding: 'var(--space-8) var(--space-6)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--space-3)',
    cursor: 'pointer',
    transition: 'border-color 0.15s, background 0.15s',
  },
  dropzoneDragging: {
    borderColor: 'var(--color-forest)',
    background: 'var(--color-mint-dark)',
  },
  uploadIcon: {
    opacity: 0.5,
  },
  dropText: {
    fontFamily: 'var(--font-body)',
    fontSize: 15,
    fontWeight: 500,
    color: 'var(--color-forest)',
    opacity: 0.75,
  },
  errorMsg: {
    fontFamily: 'var(--font-body)',
    fontSize: 15,
    color: 'var(--color-overdue)',
    background: '#fdf0ee',
    border: '1px solid var(--color-overdue)',
    borderRadius: 'var(--radius-sm)',
    padding: '8px 12px',
    marginBottom: 'var(--space-4)',
  },
  browseBtn: {
    padding: '8px 24px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 15,
    fontWeight: 700,
    fontFamily: 'var(--font-body)',
    color: 'var(--color-forest)',
    background: 'var(--color-mint-dark)',
    border: '1.5px solid var(--color-mint-dark)',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
};
